import { ROUTE_PATHS } from "./routes";
import { getCDNURL } from "@/shared/utils/commons";

/**
 * Navbar V2 data — India (en) locale only.
 *
 * Matches the new Figma design: Products / Resources / Pricing top-level,
 * with a 3-column mega-menu for Products (Products You Can Sell, Feature
 * Highlights, AI Features) and a 2-column mega-menu for Resources
 * (Resources, Graphy as a…).
 *
 * Icon keys are short slugs (e.g. "book-open") that NavbarV2 maps to
 * concrete FontAwesome Pro Regular IconDefinitions. Keeping the data
 * layer free of FontAwesome imports avoids coupling this file to the
 * icon library.
 *
 * Only items whose target page exists in this codebase carry an `href`.
 * Items without a landing page (e.g. "Coaching — coming soon", or
 * marketing pages that haven't been built) intentionally omit `href` so
 * the MenuItemRow renders as a non-clickable row.
 */

export type NavV2Badge = "new" | "coming-soon";

export type NavV2MenuItem = {
  /** Short key looked up in NavbarV2's iconMap → IconDefinition. */
  icon?: string;
  /** When present, the icon slot renders a circular color chip with a
   *  single letter (used for the "Graphy as a X" brand alternatives). */
  brand?: { letter: string; bg: string; color?: string; image?: string };
  name: string;
  description: string;
  href?: string;
  badge?: NavV2Badge;
};

export type NavV2MenuColumn = {
  heading: string;
  items: NavV2MenuItem[];
};

export type NavV2Item =
  | {
      type: "menu";
      name: string;
      columns: NavV2MenuColumn[];
    }
  | {
      type: "link";
      name: string;
      href: string;
    };

export type NavbarV2DataType = {
  items: NavV2Item[];
};

export const navbarV2Data: NavbarV2DataType = {
  items: [
    {
      type: "menu",
      name: "Products",
      columns: [
        {
          heading: "Products You Can Sell",
          items: [
            {
              icon: "book-open",
              name: "Courses",
              description: "Create and sell live or recorded courses.",
              href: ROUTE_PATHS.PRODUCTS.courses,
            },
            {
              icon: "calendar",
              name: "Memberships",
              description: "Create and sell recurring memberships.",
              href: ROUTE_PATHS.PRODUCTS.membership,
            },
            {
              icon: "video",
              name: "Webinars",
              description: "Launch and host free or paid webinars.",
              href: ROUTE_PATHS.PRODUCTS.webinar,
            },
            {
              icon: "users",
              name: "Communities",
              description:
                "Create and manage your free and paid communities.",
              href: ROUTE_PATHS.PRODUCTS.telegramCommunity,
            },
            {
              icon: "file",
              name: "Digital Products",
              description:
                "Host and sell documents, links, text, images, videos and more.",
              href: ROUTE_PATHS.PRODUCTS.digitalProducts,
            },
            {
              icon: "wand-magic-sparkles",
              name: "AI Avatar",
              description:
                "Create a digital clone of yourself and monetize your interactions.",
              href: ROUTE_PATHS.PRODUCTS.aiavatar,
            },
            {
              icon: "user-headset",
              name: "Coaching",
              description: "Offer 1:1 or group coaching.",
              href: ROUTE_PATHS.PRODUCTS.coaching,
            },
          ],
        },
        {
          heading: "Feature Highlights",
          items: [
            {
              icon: "mobile-screen",
              name: "Branded Mobile App",
              description: "Your own fully branded iOS & Android apps.",
              href: ROUTE_PATHS.FEATURES.MobileApp,
            },
            {
              icon: "money-bill-1",
              name: "Integrated Payments",
              description:
                "Accept local and global payments with country specific pricing.",
              href: ROUTE_PATHS.FEATURES.IntegratePaymentGateway,
            },
            {
              icon: "megaphone",
              name: "Marketing Tools",
              description:
                "Conversion-optimized pages, funnels, campaigns and more.",
              href: ROUTE_PATHS.FEATURES.MarketingTools,
            },
            {
              icon: "shield",
              name: "DRM Security",
              description:
                "DRM-protected content with enterprise-grade security.",
              href: ROUTE_PATHS.FEATURES.SecureVideoStreaming,
            },
            {
              icon: "link",
              name: "Third Party Integrations",
              description: "Integrate seamlessly with third-party tools.",
              href: ROUTE_PATHS.FEATURES.ThirdPartySupport,
            },
          ],
        },
        {
          heading: "AI Features",
          items: [
            {
              icon: "globe",
              name: "AI Website Builder",
              description: "Build a high-converting website using AI.",
              badge: "new",
              href: ROUTE_PATHS.FEATURES.WebsiteBuilder,
            },
            {
              icon: "infinity",
              name: "AI Agents",
              description: "Automate sales, support, and marketing.",
              badge: "new",
              href: ROUTE_PATHS.FEATURES.aiagent,
            },
            {
              icon: "brain",
              name: "AI Brain",
              description: "Train your AI brain to power all AI features.",
              badge: "new",
              href: ROUTE_PATHS.FEATURES.aibrain,
            },
          ],
        },
      ],
    },
    {
      type: "menu",
      name: "Resources",
      columns: [
        {
          heading: "Resources",
          items: [
            {
              icon: "book-open",
              name: "Blog",
              description:
                "Learn proven tips and industry best practices to scale your business.",
              href: "https://graphy.com/blog",
            },
            {
              icon: "circle-play",
              name: "Product Videos",
              description:
                "Watch product walk-throughs and feature demos.",
              href: "https://trainings.graphy.com/",
            },
            {
              icon: "screen-users",
              name: "Knowledge Base",
              description:
                "Access guides, FAQs, and product documentation.",
              href: "https://help.graphy.com",
            },
            {
              icon: "browsers",
              name: "Website Templates",
              description:
                "Explore high-converting website templates built with AI.",
              badge: "new",
              href: "/website-templates",
            },
            {
              icon: "calendar",
              name: "Partner Program",
              description: "Earn by referring creators to Graphy.",
              href: "https://partners.ongraphy.com/",
            },
          ],
        },
        {
          heading: "Graphy as a",
          items: [
            {
              brand: { letter: "t", bg: "#CCFF00", color: "#0C0C0C", image: `${getCDNURL()}graphy-web/V4/images/Alternative Pages/Teachable Alternative/Hero Section/teachable symbol.png` },
              name: "Teachable Alternative",
              description: "Explore a smarter alternative to Teachable.",
              href: "/teachable-alternative",
            },
            {
              brand: { letter: "k", bg: "#E72D2C", color: "#FFFFFF", image: `${getCDNURL()}graphy-web/V4/images/Alternative Pages/Kajabi Alternative/Hero Section/kajabi symbol.png` },
              name: "Kajabi Alternative",
              description: "Explore a smarter alternative to Kajabi.",
              href: "/kajabi-alternative",
            },
            {
              brand: { letter: "T", bg: "#F4F4F4", color: "#0C0C0C", image: `${getCDNURL()}graphy-web/V4/images/Alternative Pages/Thinkific Alternative/Hero Section/thinkific symbol.png` },
              name: "Thinkific Alternative",
              description: "Explore a smarter alternative to Thinkific.",
              href: "/thinkific-alternative",
            },
            {
              brand: { letter: "P", bg: "#E8D6F5", color: "#6D28A5", image: `${getCDNURL()}graphy-web/V4/images/Alternative Pages/Podia Alternative/Hero Section/podia symbol.png` },
              name: "Podia Alternative",
              description: "Explore a smarter alternative to Podia.",
              href: "/podia-alternative",
            },
            {
              brand: { letter: "G", bg: "#FF90E8", color: "#0C0C0C", image: `${getCDNURL()}graphy-web/V4/images/Alternative Pages/Gumroad Alternative/hero section/Gumroad symbol.png` },
              name: "Gumroad Alternative",
              description: "Explore a smarter alternative to Gumroad.",
              href: "/gumroad-alternative",
            },
            {
              brand: { letter: "L", bg: "#0C0C0C", color: "#FFFFFF", image: `${getCDNURL()}graphy-web/V4/images/Alternative Pages/Learnsworlds Alternative/Hero Section/Learnsworld symbol.png` },
              name: "Learnworlds Alternative",
              description: "Explore a smarter alternative to LearnWorlds.",
              href: "/learnworlds-alternative",
            },
          ],
        },
      ],
    },
    {
      type: "link",
      name: "Pricing",
      href: ROUTE_PATHS.PRICING,
    },
  ],
};
